/**
 * DocsContent
 * 
 * Renders markdown documentation with syntax highlighting.
 */
import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faCheck, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import type { DocPage } from '../../docs/types';
import { useDocsStore } from '../../store/docsStore';
import { getCategoryConfig } from '../../docs';
import { useDarkMode } from '../../hooks/ui/useDarkMode';

interface DocsContentProps {
    page: DocPage;
}

// Code block with copy button
const CodeBlock: React.FC<{ language: string; children: string }> = ({
    language,
    children
}) => {
    const [copied, setCopied] = React.useState(false);
    const isDark = useDarkMode();

    const handleCopy = async () => {
        await navigator.clipboard.writeText(children);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="relative group my-4">
            <button
                onClick={handleCopy}
                className="absolute right-2 top-2 p-1.5 rounded bg-gray-200 dark:bg-white/10 text-gray-500 dark:text-gray-400 
                   opacity-0 group-hover:opacity-100 transition-opacity
                   hover:bg-gray-300 dark:hover:bg-white/20 hover:text-gray-900 dark:hover:text-white"
                title="Copy code"
            >
                <FontAwesomeIcon
                    icon={copied ? faCheck : faCopy}
                    className={`h-3 w-3 ${copied ? 'text-green-500 dark:text-green-400' : ''}`}
                />
            </button>
            <SyntaxHighlighter
                style={isDark ? oneDark : oneLight}
                language={language}
                PreTag="div"
                customStyle={{
                    margin: 0,
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                }}
            >
                {children}
            </SyntaxHighlighter>
        </div>
    );
};

export const DocsContent: React.FC<DocsContentProps> = ({ page }) => {
    const { navigateTo } = useDocsStore();
    const categoryConfig = getCategoryConfig(page.category);

    // Handle internal doc links
    const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        const href = e.currentTarget.getAttribute('href');
        if (href?.startsWith('/docs/')) {
            e.preventDefault();
            const docId = href.replace('/docs/', '');
            navigateTo(docId);
        }
    };

    return (
        <article className="p-6 max-w-none">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-xs text-gray-500 mb-4">
                <span>{categoryConfig?.label || page.category}</span>
                {page.subcategory && (
                    <>
                        <FontAwesomeIcon icon={faArrowRight} className="h-2 w-2" />
                        <span className="capitalize">{page.subcategory}</span>
                    </>
                )}
            </div>

            {/* Markdown content */}
            <div className="docs-markdown text-gray-700 dark:text-gray-300">
                <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                        // Headings
                        h1: ({ children }) => (
                            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-200 dark:border-white/[0.06]">
                                {children}
                            </h1>
                        ),
                        h2: ({ children }) => (
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mt-6 mb-3">
                                {children}
                            </h2>
                        ),
                        h3: ({ children }) => (
                            <h3 className="text-base font-semibold text-gray-900 dark:text-white mt-4 mb-2">
                                {children}
                            </h3>
                        ),
                        // Paragraphs
                        p: ({ children }) => (
                            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                                {children}
                            </p>
                        ),
                        // Links
                        a: ({ href, children, ...props }) => {
                            const isInternal = href?.startsWith('/docs/');
                            return (
                                <a
                                    href={href}
                                    onClick={isInternal ? handleLinkClick : undefined}
                                    className="text-cyan-600 dark:text-cyan-400 hover:underline"
                                    {...props}
                                >
                                    {children}
                                </a>
                            );
                        },
                        // Strong/bold
                        strong: ({ children }) => (
                            <strong className="font-semibold text-gray-900 dark:text-white">{children}</strong>
                        ),
                        // Inline code
                        code({ className, children, ...props }) {
                            const match = /language-(\w+)/.exec(className || '');
                            const isInline = !match;

                            if (isInline) {
                                return (
                                    <code className="text-cyan-700 dark:text-cyan-300 bg-gray-100 dark:bg-white/10 px-1.5 py-0.5 rounded text-sm" {...props}>
                                        {children}
                                    </code>
                                );
                            }

                            return (
                                <CodeBlock language={match[1]}>
                                    {String(children).replace(/\n$/, '')}
                                </CodeBlock>
                            );
                        },
                        // Lists
                        ul: ({ children }) => (
                            <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 mb-4 space-y-1">
                                {children}
                            </ul>
                        ),
                        ol: ({ children }) => (
                            <ol className="list-decimal list-inside text-gray-700 dark:text-gray-300 mb-4 space-y-1">
                                {children}
                            </ol>
                        ),
                        li: ({ children }) => (
                            <li className="text-gray-700 dark:text-gray-300">{children}</li>
                        ),
                        // Tables
                        table: ({ children }) => (
                            <table className="w-full text-sm mb-4">{children}</table>
                        ),
                        th: ({ children }) => (
                            <th className="text-left text-gray-700 dark:text-gray-300 font-medium border-b border-gray-200 dark:border-white/[0.06] py-2 px-2">
                                {children}
                            </th>
                        ),
                        td: ({ children }) => (
                            <td className="text-gray-600 dark:text-gray-400 border-b border-gray-100 dark:border-white/[0.04] py-2 px-2">
                                {children}
                            </td>
                        ),
                        // Blockquote
                        blockquote: ({ children }) => (
                            <blockquote className="border-l-4 border-cyan-500 pl-4 italic text-gray-600 dark:text-gray-400 my-4">
                                {children}
                            </blockquote>
                        ),
                    }}
                >
                    {page.content}
                </ReactMarkdown>
            </div>

            {/* Related docs */}
            {page.related.length > 0 && (
                <div className="mt-8 pt-6 border-t border-gray-200 dark:border-white/[0.06]">
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">Related</h4>
                    <div className="flex flex-wrap gap-2">
                        {page.related.map((relatedId) => (
                            <button
                                key={relatedId}
                                onClick={() => navigateTo(relatedId)}
                                className="px-3 py-1 text-xs bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-300 rounded-full
                           hover:bg-gray-200 dark:hover:bg-white/10 hover:text-gray-900 dark:hover:text-white transition-colors"
                            >
                                {relatedId}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Tags */}
            {page.tags.length > 0 && (
                <div className="mt-6">
                    <div className="flex flex-wrap gap-1.5">
                        {page.tags.map((tag) => (
                            <span
                                key={tag}
                                className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-white/5 text-gray-500 rounded"
                            >
                                {tag}
                            </span>
                        ))}
                    </div>
                </div>
            )}
        </article>
    );
};

export default DocsContent;
